import { useState } from 'react';
import { X } from 'lucide-react';
import { adsApi } from '@/api/client';
import {
  formatRecommendationCurrency,
  formatRecommendationCurrencyPrecise,
} from '@/api/recommendationFormat';
import { campaignBudgetMin, validateCampaignBudget } from '@/utils/campaignCreate';
import { budgetPeriodFromType } from '@/utils/marketplaceBudgetLimits';
import type { CampaignActionRow } from '@/types/campaignRecommendations';

export function BudgetIncreaseModal({
  profileId,
  row,
  budgetIncreasePct,
  isOpen,
  onClose,
  onSuccess,
  onToast,
}: {
  profileId: string;
  row: CampaignActionRow;
  budgetIncreasePct: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onToast: (message: string, variant?: 'success' | 'error') => void;
}) {
  const initial =
    row.suggestedBudget != null && row.suggestedBudget > 0
      ? String(row.suggestedBudget)
      : row.dailyBudget != null
        ? String(row.dailyBudget)
        : '';
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseFloat(value);
    const budgetError = validateCampaignBudget(n, 'SP', budgetPeriodFromType(row.budgetType));
    if (budgetError) {
      setError(budgetError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await adsApi.updateSpCampaignBudget(profileId, row.campaignId, {
        budget: n,
        budgetType: row.budgetType || 'DAILY',
      });
      if (!res.success) throw new Error('Failed to update budget');
      onToast(`Daily budget updated for ${row.campaignName}.`, 'success');
      onSuccess();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to update budget');
    } finally {
      setSubmitting(false);
    }
  };

  const earnings = row.projectedAdditionalEarnings ?? 0;
  const sales = row.projectedAdditionalSales ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Increase daily budget</h3>
          <button type="button" onClick={onClose} className="text-chrome-label hover:text-foreground-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-1 text-sm text-foreground-muted">{row.campaignName}</p>
        {row.dailyBudget != null && (
          <p className="mb-3 text-sm text-foreground-subtle">
            Current budget: {formatRecommendationCurrencyPrecise(row.dailyBudget)} → suggested +
            {budgetIncreasePct}%
          </p>
        )}
        {earnings > 0 && (
          <div className="mb-4 rounded-xl border border-success/20 bg-success-subtle px-3 py-2 text-sm text-success-ink">
            At current ROAS, a +{budgetIncreasePct}% budget may earn about{' '}
            <strong>{formatRecommendationCurrency(earnings)}</strong> more (≈{' '}
            {formatRecommendationCurrency(sales)} sales on extra spend).
          </div>
        )}
        <form onSubmit={submit}>
          <label className="mb-1 block text-sm font-medium text-foreground-muted">New daily budget</label>
          <input
            type="number"
            step="0.01"
            min={campaignBudgetMin('SP', budgetPeriodFromType(row.budgetType)) ?? undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mb-2 w-full rounded-lg border border-border px-3 py-2"
            disabled={submitting}
          />
          {error && <p className="mb-3 text-sm text-error">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-border py-2 text-sm"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-success py-2 text-sm font-medium text-white disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save budget'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
